import React, { useEffect, useState, useCallback, useRef } from "react";
import { Slider } from "@/components/ui/slider";
import { useNavigate } from "react-router-dom";
import TextWall from "@/components/LearningSession/TextWall/TextWall";
import useSpeechToText from "@/speechToText";
import { usePhonemeRecognition } from "@/speechToText/usePhonemeRecognition";
import SessionResults from "@/components/LearningSession/SessionResults";
import CommandMenu from "@/components/LearningSession/CommandMenu";
import { Button } from "@/components/ui/button";
import SessionControls from "@/components/LearningSession/SessionControls";
import ControlButtons from "@/components/LearningSession/ControlButtons";
import YoutubeVideoController from "@/components/LearningSession/YoutubeVideoController";
import useTimer from "@/hooks/useTimer";
import useStore from "@/store";
import InnerContainerLoader from "@/components/InnerContainerLoader";
import useDebounce from "@/hooks/useDebounce";
import useSpeechSynthesis from "@/hooks/useSpeechSynthesis";
import { getSpeechSynthesisLanguage } from "@/constants/languageMap";
import posthog from "posthog-js";
import SessionInsightsService from "@/services/SessionInsightsService";

import "./LearningSession.css";
import PageContainer from "@/components/PageContainer";

const successSound = new Audio("/correct.mp3");
successSound.volume = 0.4;

const isMobileMode = () => {
  if (typeof window !== "undefined") {
    return window.innerWidth <= 768;
  }
  return false;
};

function LearningSession() {
  const navigate = useNavigate();
  const { user, setCurrentCollectionId } = useStore().user;
  const { soundEffects, settings } = useStore().settings;
  const {
    collections,
    fetchCollections,
    setCurrentCollectionById,
    collectionContents,
    currentCollection,
    currentContent,
    setCurrentContent,
  } = useStore().collections;

  const {
    fetchActiveReadingPosition,
    readingPosition,
    updateReadingPosition,
    updatePositionInState,
    upsertReadingPosition,
    setReadingPosition,
  } = useStore().learningSessionConfig;

  const learningSessionRef = useRef(null);
  const { elapsedTime, startTiming, pauseTiming, stopAndLogTiming } =
    useTimer();

  const [elapsedPhonemeTime, setElapsedPhonemeTime] = useState(0);

  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem("mode");
    return savedMode ? savedMode : "phoneme";
  });

  const [open, setOpen] = React.useState(false);
  const [displayResults, setDisplayResults] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [noCollectionsFound, setNoCollectionsFound] = useState(false);
  const [collectionsFetched, setCollectionsFetched] = useState(false);

  const [continousPlaybackEnabled, setContinuousPlaybackEnabled] =
    useState(false);

  const [sliderValue, setSliderValue] = useState(readingPosition.position);

  const totalCollectionCards = collectionContents?.length ?? 0;
  const rawCurrentCardNumber =
    typeof readingPosition?.position === "number"
      ? readingPosition.position + 1
      : 0;
  const currentCardNumber = totalCollectionCards
    ? Math.min(rawCurrentCardNumber, totalCollectionCards)
    : rawCurrentCardNumber;

  const {
    error,
    interimResult,
    isRecording,
    results,
    resultText,
    startSpeechToText,
    stopSpeechToText,
    resetSpeakingSession,
  } = useSpeechToText({
    continuous: true,
    crossBrowser: true,
    googleApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
    speechRecognitionProperties: {
      interimResults: true,
      lang: getSpeechSynthesisLanguage(currentCollection?.language),
      maxAlternatives: 4,
    },
    useLegacyResults: false,
  });

  const phonemeHook = usePhonemeRecognition();

  const {
    activeSentence,
    isListening,
    isGenerating,
    startListening,
    stopListening,
  } = useSpeechSynthesis([currentContent?.text] || []);

  const [accuracy, setAccuracy] = useState(0); // Assuming you have a way to calculate this
  const [wordsPerMinute, setWordsPerMinute] = useState(0);
  const [totalWords, setTotalWords] = useState(0); // Total words in the transcribed text
  const [fullRepeatCount, setFullRepeatCount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [shouldReplay, setShouldReplay] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [aiInsight, setAiInsight] = useState(null);
  const [aiInsightStatus, setAiInsightStatus] = useState("idle");
  const [sessionWordSnapshot, setSessionWordSnapshot] = useState({
    originalWords: [],
    transcribedWords: [],
    wrongWords: [],
  });

  const updateCurrentCollectionIndexInBackend = useCallback(
    async (newIndex) => {
      try {
        if (!user.current_collection_id) {
          return;
        }
        const targetContent = collectionContents?.[newIndex];
        await upsertReadingPosition(
          user.current_collection_id,
          targetContent?.id ?? null,
          newIndex,
          true
        );
      } catch (error) {
        console.error("Failed to persist reading position:", error);
      }
    },
    [user?.current_collection_id, collectionContents, upsertReadingPosition]
  );

  const debouncedUpdateIndex = useDebounce(
    updateCurrentCollectionIndexInBackend,
    1000
  );

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    const flushPending = () => {
      if (typeof debouncedUpdateIndex.flush === "function") {
        debouncedUpdateIndex.flush();
      }
    };

    const handleBeforeUnload = () => {
      flushPending();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flushPending();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      flushPending();
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [debouncedUpdateIndex]);

  const handleVideoEndReached = () => {
    handleCollectionNavigation("next");
  };

  useEffect(() => {
    localStorage.setItem("mode", mode);
  }, [mode]);

  useEffect(() => {
    // Ensure the slider updates its position if the readingPosition updates elsewhere
    setSliderValue(readingPosition.position);
  }, [readingPosition.position]);

  useEffect(() => {
    const fetchAndSetCollections = async () => {
      if (user && user.id) {
        await fetchCollections(user.id);
        setCollectionsFetched(true);
      }
    };

    fetchAndSetCollections();
  }, [user, fetchCollections]);

  useEffect(() => {
    if (collectionsFetched) {
      setNoCollectionsFound(collections.length === 0);
    }
  }, [collectionsFetched, collections.length]);

  useEffect(() => {
    if (
      user &&
      collectionsFetched &&
      collections.length > 0 &&
      !user.current_collection_id
    ) {
      setCurrentCollectionId(collections[0].id);
    }
  }, [
    user,
    collectionsFetched,
    collections,
    user?.current_collection_id,
    setCurrentCollectionId,
  ]);

  useEffect(() => {
    handleReset();
  }, [mode]);

  useEffect(() => {
    handleReset();
    setIsLoading(true);

    const fetchAndSetCurrentCollection = async () => {
      if (!user?.current_collection_id) {
        setIsLoading(false);
        return;
      }

      console.log(
        "fetching reading position for user.current_collection_id",
        user.current_collection_id
      );

      try {
        const [collectionContents, fetchedReadingPosition] = await Promise.all([
          setCurrentCollectionById(user.current_collection_id),
          fetchActiveReadingPosition(user.current_collection_id),
        ]);

        if (!collectionContents || collectionContents.length === 0) {
          if (collections.length > 0) {
            const fallbackCollection =
              collections.find(
                (collection) => collection.id !== user.current_collection_id
              ) ?? collections[0];

            if (
              fallbackCollection &&
              fallbackCollection.id !== user.current_collection_id
            ) {
              setCurrentCollectionId(fallbackCollection.id);
              return;
            }
          }

          setIsLoading(false);
          return;
        }

        let resolvedReadingPosition = fetchedReadingPosition;

        if (!resolvedReadingPosition) {
          const fallbackPosition = {
            ...readingPosition,
            collection_id: user.current_collection_id,
            content_id: collectionContents[0]?.id ?? null,
            position: 0,
            is_currently_active: true,
            last_updated: new Date().toISOString(),
            user_id: user?.id ?? readingPosition?.user_id ?? null,
          };

          setReadingPosition(fallbackPosition);
          resolvedReadingPosition = fallbackPosition;

          try {
            await upsertReadingPosition(
              user.current_collection_id,
              fallbackPosition.content_id,
              fallbackPosition.position,
              true
            );
          } catch (persistError) {
            console.error(
              "Failed to persist fallback reading position:",
              persistError
            );
          }
        }

        const clampedPosition = Math.max(
          0,
          Math.min(
            resolvedReadingPosition.position ?? 0,
            collectionContents.length - 1
          )
        );

        if (clampedPosition !== resolvedReadingPosition.position) {
          updatePositionInState(clampedPosition);
        }

        setCurrentContent(clampedPosition);
      } catch (error) {
        console.error("Failed to fetch current collection:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndSetCurrentCollection();
  }, [user?.current_collection_id, collections]);

  const accuracyMode = settings?.accuracy_mode || "easy";
  const successThreshold = accuracyMode === "hard" ? 100 : 80;

  useEffect(() => {
    if (mode === "realtime") {
      return;
    }
    if (phonemeHook.phonemes.length === 0 && displayResults) {
      setDisplayResults(false);
    }
    if (phonemeHook.phonemes.length > 0 && !displayResults) {
      setDisplayResults(true);
      setAccuracy(phonemeHook.overallAccuracy);
      stopAndLogTiming();
      setElapsedPhonemeTime(phonemeHook.elapsedTime);
      setWordsPerMinute(phonemeHook.adjustedWpm);
      setFullRepeatCount(phonemeHook.fullRepeatCount);
      setSessionWordSnapshot({
        originalWords: [],
        transcribedWords: [],
        wrongWords: [],
      });
      setAiInsight(null);
      setAiInsightStatus("idle");

      const roundedAccuracy = Math.round(phonemeHook.overallAccuracy || 0);
      if (roundedAccuracy >= successThreshold && soundEffects) {
        successSound.pause();
        successSound.currentTime = 0;
        successSound
          .play()
          .catch((error) => console.error("Failed to play the sound:", error));
      }
    }
  }, [phonemeHook, mode, soundEffects, successThreshold, displayResults]);

  useEffect(() => {
    const finalText = results
      .map((result) => result.transcript.trim())
      .join(" ");
    const wordCount = finalText.split(/\s+/).length;
    setTotalWords(wordCount);
  }, [results]);

  useEffect(() => {
    if (!displayResults || !currentContent) {
      setAiInsight(null);
      setAiInsightStatus("idle");
      return;
    }

    const sanitizedAccuracy = Number.isFinite(accuracy) ? accuracy : 0;
    const sanitizedWpm = Number.isFinite(wordsPerMinute)
      ? wordsPerMinute
      : 0;
    const activeElapsedTime =
      mode === "realtime" ? elapsedTime : elapsedPhonemeTime;

    if (!Number.isFinite(activeElapsedTime) || activeElapsedTime === 0) {
      setAiInsight(null);
      setAiInsightStatus("idle");
      return;
    }

    const payload = {
      accuracy: Math.round(sanitizedAccuracy),
      words_per_minute: Number(sanitizedWpm.toFixed(1)),
      elapsed_seconds: Math.round(activeElapsedTime),
      language: currentCollection?.language,
      content_length:
        (currentContent?.text || "")
          .trim()
          .split(/\s+/)
          .filter(Boolean).length ?? 0,
      wrong_words: sessionWordSnapshot.wrongWords.slice(0, 6),
      target_text: currentContent?.text,
      mode,
      repeatable: Boolean(currentContent?.repeatable),
    };

    if (mode === "realtime") {
      const stitchedTranscription =
        results
          .map((result) => result.transcript.trim())
          .join(" ")
          .trim() || resultText?.trim();
      if (stitchedTranscription) {
        payload.transcribed_text = stitchedTranscription;
      }
    }

    let cancelled = false;

    const fetchInsight = async () => {
      setAiInsightStatus("loading");
      try {
        const response =
          await SessionInsightsService.generateInsight(payload);
        if (cancelled) {
          return;
        }

        const message = response?.ai_insight?.trim();
        const aiAvailable = response?.ai_available;
        const aiError = response?.error;

        if (message) {
          setAiInsight(message);
          setAiInsightStatus("success");
          return;
        }

        setAiInsight(null);

        if (aiAvailable === false || aiAvailable === "false") {
          setAiInsightStatus("unavailable");
          return;
        }

        if (aiError) {
          setAiInsightStatus("error");
          return;
        }

        setAiInsightStatus("idle");
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch AI insight:", error);
          setAiInsight(null);
          setAiInsightStatus("error");
        }
      }
    };

    fetchInsight();

    return () => {
      cancelled = true;
    };
  }, [
    displayResults,
    accuracy,
    wordsPerMinute,
    elapsedTime,
    elapsedPhonemeTime,
    currentContent,
    currentCollection?.language,
    mode,
    sessionWordSnapshot,
    results,
    resultText,
  ]);

  // Handler to trigger replay from ControlButtons
  const handleReplay = useCallback(() => {
    setShouldReplay(true);
  }, []);

  // Reset shouldReplay flag after replay action is initiated
  useEffect(() => {
    if (shouldReplay) {
      setShouldReplay(false);
    }
  }, [shouldReplay]);

  const calculateWPMAndAccuracy = useCallback(
    (numCorrectWords) => {
      const minutesElapsed = elapsedTime / 60;
      if (minutesElapsed > 0) {
        const wpm = numCorrectWords / minutesElapsed;
        setWordsPerMinute(wpm);
      } else {
        setWordsPerMinute(0);
      }
    },
    [elapsedTime]
  );

  useEffect(() => {
    const processRecognition = async () => {
      // if (isProcessing && interimResult === undefined) {
      if (isProcessing) {
        // Execute the code that should run after interimResult is cleared
        if (mode === "realtime") {
          await stopSpeechToText();
        } else if (mode === "phoneme") {
          await phonemeHook.stopPhonemeRecognition();
        }
        setIsProcessing(false); // End processing
      }
    };

    processRecognition();
  }, [isProcessing, phonemeHook]);

  const handleReset = () => {
    stopAndLogTiming();
    // setActiveSentence("");
    stopListening();
    setDisplayResults(false);
    phonemeHook.cancelPhonemeRecognition();
    setAiInsight(null);
    setAiInsightStatus("idle");
    setSessionWordSnapshot({
      originalWords: [],
      transcribedWords: [],
      wrongWords: [],
    });

    if (isRecording || phonemeHook.isRecording) {
      if (mode === "realtime") {
        stopSpeechToText();
      }
    }
    resetSpeakingSession();
  };

  const handleCollectionNavigation = (navigationInput) => {
    let newIndex;
    if (typeof navigationInput === "number") {
      // Direct index selection from the slider
      newIndex = navigationInput;
    } else {
      // Prev/Next button navigation
      const currentIndex = readingPosition.position;
      const maxIndex = collectionContents.length - 1;
      if (navigationInput === "prev" && currentIndex > 0) {
        newIndex = currentIndex - 1;
      } else if (navigationInput === "next" && currentIndex < maxIndex) {
        newIndex = currentIndex + 1;
      }
    }

    if (newIndex !== undefined) {
      // Update the index in the state immediately
      updatePositionInState(newIndex);
      setCurrentContent(newIndex);
      setSliderValue(newIndex);

      // Debounce the backend update
      debouncedUpdateIndex(newIndex);

      handleReset();
    }

    setDisplayResults(false);
    stopAndLogTiming();
  };

  const handleStartStopRecording = () => {
    posthog.capture("recording_toggled");
    if (displayResults) {
      handleReset();
    }
    if (isRecording || phonemeHook.isRecording) {
      setIsProcessing(true);
      pauseTiming();
    } else {
      startTiming();
      if (mode === "realtime") {
        startSpeechToText();
      } else if (mode === "phoneme") {
        phonemeHook.startPhonemeRecognition(
          currentContent.text,
          currentCollection.language,
          currentContent.repeatable,
          true,
          { proficiency: true }
        );
      }
    }
  };

  const attemptToPauseRecording = (
    accuracy,
    originalWords,
    transcribedWords,
    wrongWords
  ) => {
    stopAndLogTiming();
    calculateWPMAndAccuracy(originalWords.length - wrongWords.length);
    setAccuracy(accuracy);
    setSessionWordSnapshot({
      originalWords,
      transcribedWords,
      wrongWords,
    });
    setAiInsight(null);
    setAiInsightStatus("idle");

    setDisplayResults(true);
    // If currently recording, set processing to true
    if (isRecording || phonemeHook.isRecording) {
      setIsProcessing(true);
    }
  };

  if (noCollectionsFound) {
    return (
      <PageContainer innerClassName="items-center justify-center">
        <div className="flex flex-col items-center gap-6 text-center">
          <div className="rounded-full border border-line bg-surface/80 px-4 py-1 text-xs uppercase tracking-[0.3em] text-fg-muted">
            Let&apos;s get started
          </div>
          <p className="max-w-sm text-sm text-fg-muted">
            No collections found yet. Create your first collection to begin your
            practice session.
          </p>
          <Button
            className="group relative overflow-hidden rounded-full border border-line bg-elevated px-6 py-2 text-sm font-semibold text-fg transition hover:border-primary/40 hover:bg-elevated"
            onClick={() => navigate("/collections/new")}
          >
            <span className="relative flex items-center gap-2">
              <span className="text-lg leading-none">+</span>
              Create collection
            </span>
          </Button>
        </div>
      </PageContainer>
    );
  }
  if (!currentCollection || !currentContent || !readingPosition) {
    return (
      <PageContainer innerClassName="items-center justify-center">
        <InnerContainerLoader offset={0} fullscreen />
      </PageContainer>
    );
  }

  return (
    <PageContainer outerClassName="mobile-safe-area" innerClassName="max-w-6xl">
      <div
        ref={learningSessionRef}
        className="relative flex flex-1 w-full flex-col gap-10"
      >
        {readingPosition.collection_id && (
          <SessionControls mode={mode} setMode={setMode} />
        )}
        {currentCollection.content_type === "youtube" && readingPosition && (
          <div className="mb-6 flex items-center justify-center rounded-3xl md:mb-10">
            <YoutubeVideoController
              parentRef={learningSessionRef}
              shouldReplay={shouldReplay}
              isVideoPlaying={isVideoPlaying}
              setIsVideoPlaying={setIsVideoPlaying}
              handleNext={handleVideoEndReached}
              continousPlaybackEnabled={continousPlaybackEnabled}
              setContinuousPlaybackEnabled={setContinuousPlaybackEnabled}
              handleCollectionNavigation={handleCollectionNavigation}
              isCollectionLoading={isLoading}
            />
          </div>
        )}

        <div className="flex flex-1 w-full flex-col items-center justify-center">
          <div
            className={`relative flex w-full items-center justify-center rounded-3xl px-8 py-10 ${
              currentCollection.content_type !== "youtube"
                ? "max-h-[50svh]"
                : "md:max-h-[24svh] max-h-[32svh]"
            } ${isLoading ? "overflow-hidden" : "overflow-auto border border-line bg-surface/80 shadow-panel"}`}
          >
            {isLoading ? (
              currentCollection.content_type === "youtube" ? (
                <div className="flex h-full w-full items-center justify-center rounded-2xl bg-transparent" />
              ) : (
                <InnerContainerLoader offset={0} fullscreen />
              )
            ) : (
              <TextWall
                text={currentContent?.text}
                activeSentence={activeSentence}
                transcribedSpeechText={results
                  .map((result) => result.transcript.trim())
                  .join(" ")}
                interimTranscribedSpeechText={interimResult?.trim() || ""}
                endOfTextReached={attemptToPauseRecording}
                phonemes={phonemeHook.phonemes}
                mode={mode}
              />
            )}
          </div>
        </div>
      </div>

      <div className="mt-10 flex w-full flex-col items-center gap-8">
        {!isLoading && (
          <div className="flex w-full max-w-xl items-center gap-4 text-sm text-fg-muted">
            <Slider
              step={1}
              max={collectionContents.length - 1}
              defaultValue={[readingPosition.position]}
              value={[sliderValue]}
              onValueChange={(value) => handleCollectionNavigation(value[0])}
              className="flex-1"
            />
            {totalCollectionCards > 0 && (
              <span className="whitespace-nowrap text-[13px] font-semibold tracking-wide text-fg-muted">
                <span className="sr-only">Current card and total cards</span>
                {currentCardNumber}/{totalCollectionCards}
              </span>
            )}
          </div>
        )}

        <ControlButtons
          mode={mode}
          onPrev={() => handleCollectionNavigation("prev")}
          onNext={() => handleCollectionNavigation("next")}
          endOfCollectionReached={
            readingPosition.position === collectionContents.length - 1
          }
          onReset={handleReset}
          onStartStopRecording={handleStartStopRecording}
          isRecording={isRecording || phonemeHook.isRecording}
          onStartStopListening={(rate) => {
            if (isListening) {
              stopListening();
            } else {
              startListening(rate);
            }
          }}
          isListening={isListening}
          isGeneratingAudio={isGenerating}
          isPredicting={phonemeHook.isPredicting}
          handleReplay={handleReplay}
          isVideoPlaying={isVideoPlaying}
          setIsVideoPlaying={setIsVideoPlaying}
        />

        {displayResults && (
          <div className="w-full max-w-5xl">
            <SessionResults
              wordsPerMinute={wordsPerMinute}
              accuracy={accuracy}
              elapsedTime={
                mode === "realtime" ? elapsedTime : elapsedPhonemeTime
              }
              repeatable={currentContent?.repeatable}
              fullRepeatCount={fullRepeatCount}
              proficiency={mode === "phoneme" ? phonemeHook.proficiency : null}
              words={mode === "phoneme" ? phonemeHook.phonemes : []}
              aiInsight={aiInsight}
              aiInsightStatus={aiInsightStatus}
            />
          </div>
        )}
      </div>

      <div className="fixed bottom-8 right-8 hidden md:flex">
        <div className="inline-flex items-center gap-2 rounded-full bg-transparent px-4 py-1.5 text-[12px] tracking-[0.18em] text-fg-subtle shadow-panel backdrop-blur-2xl font-['Verdana']">
          Actions
          <kbd className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-1 font-['Verdana'] text-[12px] leading-none text-fg-muted shadow-panel">
            <span>⌘</span>
            <span>K</span>
          </kbd>
        </div>
      </div>
      <CommandMenu open={open} setOpen={setOpen} />
    </PageContainer>
  );
}

export default LearningSession;
